import { useInboxStore } from "stores/inboxStore"
import Card from "./Card"
import Empty from "./Empty"
import FetchError from "./FetchError"
import ArchiveModal from "./Modals/ArchiveModal"
import InfiniteScroll from "react-infinite-scroll-component"
import Loader from "./Loader"
import EndMessage from "./EndMessage"
import { FetchTeeupsArgs, INBOX_TABS } from "types/inbox"
import { forwardRef, useCallback } from "react"
import { useHistory } from "react-router-dom"
import {
  joinTeeupInvitation,
  updateTeeupTrashStatus,
} from "@actions/teeupActions"
import { useSelector } from "react-redux"
import { selectUserId } from "@selectors/user"
import { TeeupItem } from "types/teeup"
import { useSnackbarStore } from "stores/snackbarStore"
import UnderlineModal from "./Modals/UnderlineModal"
import OrganizerArchive from "./Modals/OrganizerArchive"

interface Props {
  fetchTeeUps: (state: FetchTeeupsArgs, shouldClearPrevState: boolean) => void
}

const TeeUpList = ({ fetchTeeUps }: Props) => {
  const isLoading = useInboxStore(state => state.isLoading)
  const setIsLoading = useInboxStore(state => state.setIsLoading)
  // BUG4 fix: `isFetchError` was already tracked in the store but never consulted by this
  // render gate -- a failed fetch used to fall through to the same branch as "genuinely no
  // TeeUps" (see FetchError.tsx for the full root-cause note).
  const isFetchError = useInboxStore(state => state.isFetchError)
  const setIsFetchError = useInboxStore(state => state.setIsFetchError)
  const teeUps = useInboxStore(state => state.teeUps)
  const viewMode = useInboxStore(state => state.viewMode)
  const setTeeUps = useInboxStore(state => state.setTeeUps)

  const pagination = useInboxStore(state => state.pagination)
  const setPagination = useInboxStore(state => state.setPagination)
  const isArchiveTab = useInboxStore(
    state => state.currentOpenTab.value === INBOX_TABS.ARCHIVE
  )
  const currentOpenTab = useInboxStore(state => state.currentOpenTab)
  const selectedFilters = useInboxStore(state => state.selectedFilters)
  const searchValue = useInboxStore(state => state.searchValue)
  const currentPage = useInboxStore(state => state.pagination.page)
  const setSnackbarConfig = useSnackbarStore(state => state.setSnackbarConfig)
  const users = useInboxStore(store => store.users)

  const userId = useSelector(selectUserId)
  const history = useHistory()

  const handleNextPage = () => {
    setPagination({ ...pagination, page: pagination.nextPage })
  }

  const handleOpenTeeUp = useCallback(
    (teeUp: TeeupItem, removedFromById?: number | null) => {
      if (removedFromById) {
        const participant = users[removedFromById]
        if (!participant) return

        setSnackbarConfig({
          open: true,
          message: `"${participant.name}" has removed you from "${teeUp.name}"`,
        })
        return
      }
      history.push(`/teeUps/${teeUp.id}`)
    },
    [users]
  )

  const onAcceptInvitation = useCallback(
    async (teeUp: TeeupItem) => {
      if (!userId) return

      await joinTeeupInvitation({
        teeupId: teeUp.id,
        userId,
        categoryId: teeUp.categoryId,
      })
      handleOpenTeeUp(teeUp)
    },
    [userId]
  )

  const onDismissInvitation = useCallback(async (teeUp: TeeupItem) => {
    await updateTeeupTrashStatus(teeUp.id)
    setIsLoading(true)
    setTeeUps([])
    fetchTeeUps(
      { currentOpenTab, selectedFilters, searchValue, currentPage },
      true
    )
  }, [])

  const onPreviewClick = useCallback(async (teeUp: TeeupItem) => {
    history.push(`/teeUps/${teeUp.id}`)
  }, [])

  // BUG4 fix: priority order matters -- an in-flight/errored fetch must never read as "empty".
  if (isFetchError && teeUps.length === 0)
    return (
      <FetchError
        onRetry={() => {
          setIsFetchError(false)
          setIsLoading(true)
          setTeeUps([])
          fetchTeeUps(
            { currentOpenTab, selectedFilters, searchValue, currentPage },
            true
          )
        }}
      />
    )
  if (!isLoading && teeUps.length === 0) return <Empty />
  else if (isLoading && teeUps.length === 0) return <Loader />

  return (
    <div>
      {/* BUG3 fix: this was `xs:w-[99vw]` -- sized off the VIEWPORT width, not this element's
          actual box (the window-shell's `.main-window`, which shrinks when the CooweAI side
          panel opens -- see index.scss `.app.window-shell > .main-window`). 99vw stayed wider
          than main-window's shrunk width, so at `md:max-w-[1200px]` the wrapper (and every grid
          column inside it) kept its full desktop width and simply ran out under/behind the AI
          panel instead of shrinking with its real container. `w-full` makes it track whatever
          width its actual parent gives it (main-window when the panel's open, the full page
          otherwise); `md:max-w-[1200px]` still caps it on very wide screens. */}
      <div
        className={
          "mx-auto my-0 w-full xs:mt-3 md:mt-8 xs:px-3 md:px-6 " +
          (viewMode === "grid" ? "md:max-w-[1200px]" : "md:max-w-[840px]")
        }>
        <InfiniteScroll
          dataLength={teeUps.length}
          className={
            (viewMode === "grid"
              ? "grid md:grid-cols-2 xl:grid-cols-3 gap-4 items-start"
              : "flex flex-col gap-2.5") + " !overflow-visible"
          }
          scrollableTarget="infinite-scroll-target"
          next={handleNextPage}
          hasMore={teeUps.length < pagination.total}
          scrollThreshold={0.8}
          loader={<Loader />}
          endMessage={<EndMessage />}>
          {teeUps.map(teeUp => (
            <Card
              teeUp={teeUp}
              key={teeUp.id}
              isArchiveTab={isArchiveTab}
              onClick={handleOpenTeeUp}
              onAcceptInvitation={onAcceptInvitation}
              onDismissInvitation={onDismissInvitation}
              onPreviewClick={onPreviewClick}
              refetchTeeUps={() => {
                setTeeUps([])
                fetchTeeUps(
                  { currentOpenTab, selectedFilters, searchValue, currentPage },
                  true
                )
              }}
            />
          ))}
        </InfiniteScroll>
      </div>

      <ArchiveModal />
      <OrganizerArchive />
    </div>
  )
}

export default TeeUpList
